import React, { Component, ErrorInfo, ReactNode } from 'react';
import { withTranslation } from 'react-i18next';
import { compose } from 'react-recompose';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router';
import html2canvas from 'html2canvas';
import type { TFunction } from 'i18next';
import StackTracey from 'stacktracey';
import ErrorImg from '@/assets/images/critical-error-logo.png';
import AlertSnackbar from '@/components/UI/AlertSnackbar/AlertSnackbar';
import Button from '@/components/UI/Button/Button';
import ConfirmDialog from '@/components/UI/Dialog/ConfirmDialog';
import Spinner from '@/components/UI/Spinner/Spinner';
import ToastContext from '@/contexts/ToastContext';
import { ENV_MAP, VERSION_DESCRIPTION, env, ROUTES } from '@/helpers/constants';
import { getSettingsInfo, getUserInfo } from '@/redux/User/user.reducer';
import { User, UserSettings } from '@/types/User';
import styles from './ErrorBoundary.module.scss';
import packageInfo from '@/../package.json';

interface ErrorReport {
  summary: string;
  jiraDescription: string;
  serviceNowDescription: string;
  serviceNowDescription2: string;
  copyDataDescription?: string;
  environment?: string;
}

interface State {
  errorServiceError: boolean;
  hasError: boolean;
  loading: boolean;
  message: string;
  jiraTicketLink?: string;
  serviceNowTicketLink?: string;
  errorReport?: ErrorReport;
  backToDashboardClicked: boolean;
}

interface Props {
  user: User;
  settingsInfo: UserSettings;
  children: ReactNode;
  t: TFunction;
  navigate: (path: string) => void;
}

const getStorageAppVersion = () => window.localStorage.getItem('APP_VERSION');

function checkVisible(elm: HTMLElement) {
  const rect = elm.getBoundingClientRect();
  const viewHeight = Math.max(window.document.documentElement.clientHeight, window.innerHeight);

  return !(rect.bottom < 0 || rect.top - viewHeight >= 0);
}

class ErrorBoundary extends Component<Props, State> {
  state: State = {
    errorServiceError: false,
    hasError: false,
    loading: false,
    message: '',
    errorReport: undefined,
    backToDashboardClicked: false,
  };

  static contextType: any = ToastContext;

  static getDerivedStateFromError(error: Error) {
    return { hasError: true };
  }

  async componentDidCatch(error: Error, info: ErrorInfo) {
    const summary = error.message;
    this.generateComponentStack(error)
      .then((componentStackTraceProps) => {
        const { componentStackTrace, component } = componentStackTraceProps;
        const environment = ENV_MAP[env] || 'UNKNOWN';
        const report = {
          summary,
          jiraDescription:
            `*User Details:* \n- User Code: ${this.props.user.userCode}\n
                Role: ${this.props.user.roleSelected}\n
                All User Roles: ${this.props.user.rolesList}\n
                User Departament:: ${this.props.user.department}\n\n
                *User timezone/lang:* ${this.props.settingsInfo.timezone} - ${this.props.settingsInfo.language}\n\n`
                + `*App version:* ${packageInfo.version} - ${VERSION_DESCRIPTION} (${getStorageAppVersion()})\n\n`
                + `*Environment:* ${environment}\n\n`
                + `*Current URL:* ${window.location.href}\n\n`
                + `*Timestamp UTC:* ${new Date().toUTCString()}\n\n`
                + `*Component:* ${component}\n\n`
                + `*Error Message:* ${summary}\n\n`
                + `*Stack Trace:* \n{code:java}${componentStackTrace}{code}`,
          serviceNowDescription:
            `<span><b>User Code: </b>${this.props.user.userCode}</span>
              <span><b>User Role: </b>${this.props.user.roleSelected}</span>
              <span><b>All User Roles: </b> ${this.props.user.rolesList}</span>
              <span><b>User Departament: </b> ${this.props.user.department}</span></br>
              <span><b>User timezone/lang: </b>${this.props.settingsInfo.timezone} - ${this.props.settingsInfo.language}</span></br>
              <span><b>App version: </b>${packageInfo.version} - ${VERSION_DESCRIPTION} (${getStorageAppVersion()})</span></br>
              <span><b>Error message: </b>${summary}</span></br>
              <span><b>Component: </b>${component}</span></br>
              <span><b>Current URL: </b>${window.location.href}</span></br>
              <span><b>Environment: </b>${environment}</span></br>
              <span><b>Timestamp UTC: </b>${new Date().toUTCString()}</span></br>
              <div id="errorboundary-locator"></div>
              `,
          serviceNowDescription2:
            `<b>Stack Trace:</b><pre>${componentStackTrace}</pre><br/>`,
          environment,
          copyDataDescription: '',
        };
        // remove html tags
        report.copyDataDescription = `${report.serviceNowDescription} \n ${report.serviceNowDescription2}`.replace(/(<([^>]+)>)/gi, '');
        this.setState({ errorReport: report });

        /* ENABLE IN PART II
          if (env !== 'local') {
             this.reportError(report);
          }
        */
      }).catch((error) => {
        console.log('error', error);
      });
  }

  generateComponentStack = async (error: Error): Promise<any> => {
    const stack = new StackTracey(error.stack);
    const parsedStack = await stack.withSourcesAsync();
    console.log('CRITICAL ERROR DETECTED, please copy and paste the following information into the support email:', parsedStack);
    const component = parsedStack?.items?.[0]?.fileName || parsedStack?.items?.[0]?.file || parsedStack?.items?.[1]?.fileName
      || parsedStack?.items?.[1]?.file || 'UNKNOWN';
    const componentStack: any = parsedStack?.items?.map((item): any =>
      `${item.sourceLine ? `Source Line: ${item.sourceLine.slice(0, 55)}\n` : ''}`
      + `${item.column ? `Column number: ${item.column} -` : ''} ${item.line ? `Line number: ${item.line} \n` : ''} `
      + `${item.file ? `File name: ${item.file} \n` : ''}`
      + `${item.fileName ? `Function name: ${item.fileName}\n` : ''} ${item.callee ? `Callee: ${item.callee}\n` : ''} \n`) ?? [];

    return { component, componentStackTrace: componentStack.join('') };
  };

  resetErrorBoundary = () => {
    this.setState({
      errorServiceError: false,
      hasError: false,
      loading: false,
      message: '',
      jiraTicketLink: '',
      serviceNowTicketLink: '',
      backToDashboardClicked: false,
    });
    this.props.navigate(ROUTES.DASHBOARD);
  };

  onbackToDashboardClicked = () => {
    this.setState({ backToDashboardClicked: true });
  };

  copyDataToClipboard = async (addToast) => {
    const toCopy = this.state.errorReport?.copyDataDescription || '';

    try {
      await navigator.clipboard.writeText(toCopy);
      addToast(this.props.t('errorBoundary.errorBoundaryCopySuccess'), 'Success');
    } catch (err) {
      console.error('Failed to copy: ', err);
      addToast(this.props.t('errorBoundary.errorBoundaryCopyError'), 'Warning');
    }
  };

  downloadScreenshot = async () => {
    const [containerId, locatorId, imageId] = ['error-boundary-container', 'errorboundary-locator', 'error-boundary-image'];
    const [container, locator, image] = [document.getElementById(containerId), document.getElementById(locatorId), document.getElementById(imageId)];

    if (container) {
      if (locator && image) {
        while (!checkVisible(locator)) {
          container.style.fontSize = `${parseFloat(container.style.fontSize || '100') - 2}%`;
          image.style.height = `${parseFloat(image.style.height || '20') - 1}%`;
          await new Promise((resolve) => setTimeout(resolve, 50));
        }
      }
      const canvas = await html2canvas(container);
      const link = document.createElement('a');
      link.download = 'screenshot-error-CaRT.png';
      link.href = canvas.toDataURL();
      link.click();
      container.style.fontSize = '100%';

      if (image) {
        image.style.height = '20%';
      }
    }
  };

  render() {
    // @ts-expect-error:next-line
    const { addToast } = this.context;

    return (
      <>
        {this.state.hasError
          ? (
            <section id="error-boundary-container" className={styles.Content}>
              <img id="error-boundary-image" src={ErrorImg} alt="Critical error" />
              <h1>{this.props.t('errors.oops')}</h1>
              <div className={styles.Message}>
                <p>{this.props.t('errorBoundary.errorBoundaryMessage1')}</p>
                <p dangerouslySetInnerHTML={{ __html: this.props.t('errorBoundary.errorBoundaryMessage3') }} />
              </div>
              {
                this.state.errorReport?.serviceNowDescription && (
                  <div className={styles.StackTraceContainer}>
                    <div className={styles.StackTrace} dangerouslySetInnerHTML={{ __html: this.state.errorReport?.serviceNowDescription }} />
                    <div className={styles.StackTrace} dangerouslySetInnerHTML={{ __html: this.state.errorReport?.serviceNowDescription2 }} />
                  </div>
                )
              }
              {(this.state.loading || !this.state.errorReport) && <Spinner isVisible />}
              <div className={styles.BottomButtons}>
                <Button
                  id="copy-data-to-clipboard-button"
                  buttonStyle="Secondary"
                  btnText={this.props.t('errorBoundary.copyDataToClipboard')}
                  handleClick={() => this.copyDataToClipboard(addToast)}
                />
                <Button
                  id="back-to-dashboard-button"
                  buttonStyle="Primary"
                  btnText={this.props.t('errorBoundary.backToDashboard')}
                  handleClick={() => this.setState({ backToDashboardClicked: true })}
                />
                <Button
                  id="download-screenshot-button"
                  buttonStyle="Secondary"
                  btnText={this.props.t('errorBoundary.downloadScreenshot')}
                  handleClick={this.downloadScreenshot}
                />
              </div>

              {this.state.message && (
                <AlertSnackbar
                  id="error-boundary-snackbar"
                  open
                  onClose={this.resetErrorBoundary}
                  severity={this.state.errorServiceError ? 'Error' : 'Warning'}
                >
                  {this.state.message}
                </AlertSnackbar>
              )}

              {this.state.backToDashboardClicked && (
                <ConfirmDialog
                  confirm={this.resetErrorBoundary}
                  close={() => this.setState({ backToDashboardClicked: false })}
                  message={this.props.t('errorBoundary.backToDashboardMessage')}
                  confirmBtnText={this.props.t('errorBoundary.backToDashboard')}
                />
              )}
            </section>
          )
          : this.props.children}
      </>
    );
  }
}

function mapStateToProps(state) {
  return {
    user: getUserInfo(state),
    settingsInfo: getSettingsInfo(state),
  };
}

const ErrorBoundaryWithRouter = (props: Omit<Props, 'navigate'>) => {
  const navigate = useNavigate();

  return <ErrorBoundary {...props} navigate={navigate} />;
};

export default compose(
  withTranslation(),
  connect(mapStateToProps),
)(ErrorBoundaryWithRouter);
